<template>
  <div
    class="popup-wrapper"
    :class="{
      'is-open': show,
      'height-auto': fitContentHeight,
    }"
  >
    <slot
      name="action"
      :visible="show"
      :show="() => show = true"
      :hide="() => show = false"
      :toggle="() => show = !show"
    />
    <div
      v-if="show"
      class="popup"
      ref="popup"
    >
      <slot
        :visible="show"
        :show="() => show = true"
        :hide="() => show = false"
        :toggle="() => show = !show"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { ref, watch } from 'vue';

import { freezeBody } from '@/utilities/FreezeBody';

const props = withDefaults(defineProps<{
  value?: boolean,
  fitContentHeight: boolean,
}>(), {
  value: false,
  fitContentHeight: false,
});

const emit = defineEmits<{
  (event: 'show'): void,
  (event: 'hide'): void,
  (event: 'input', value: boolean): void,
}>();

const show = ref<boolean>(props.value);
watch(() => props.value, () => show.value = props.value);

watch(show, () => {
  if (show.value) {
    emit('show');
  } else {
    emit('hide');
  }
  freezeBody(false, false);
  if (window.innerWidth < 1000) {
    freezeBody(show.value);
  }
  emit('input', show.value);
});

const popup = ref(null);
onClickOutside(popup, () => show.value = false);
</script>

<style lang="scss" scoped>
@import "@scss-vars";

.popup-wrapper {
  position: relative;

  @media screen and (max-width: $screen-md-max) {
    &.is-open:before {
      content: '';
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 99;
      background-color: $black-50;
    }
  }

  &.height-auto .popup{
    height: fit-content;
  }
}

.popup {
  border: 1px solid $black;
  border-radius: 10px;
  background-color: $white;
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 10;
  height: 440px;
  min-width: 100%;
  overflow: hidden;

  @media screen and (max-width: $screen-md-max) {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    height: calc(100vh - #{rem(200)});
    transform: translateY(100vh);
    border-color: $white;
    border-radius: rem(10) rem(10) 0 0;
    background-color: $white;
    display: flex;
    flex-direction: column;
    transition: opacity 0.2s linear, transform 0.2s linear,
    visibility 0s linear 0.2s;

    .is-open .popup + &, .is-open & {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      transition: opacity 0.2s linear, transform 0.2s linear,
      visibility 0s linear 0s;
    }
  }

  @media screen and (max-height: 500px) {
    height: calc(100vh - #{rem(32)});
  }
}
</style>
